import React, { useEffect, useState } from 'react';
import { collection, getCountFromServer, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { isAdminEmail } from '../lib/admins';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Tent, Store, FileText, MessageSquare, RefreshCw, Star, Activity, Euro, ChevronRight, CheckCircle2, XCircle, Clock, ExternalLink, Handshake, Trash2, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function AdminDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFestivals: 0,
    totalStands: 0,
    totalApplications: 0,
    totalConversations: 0,
    totalFavorites: 0,
    publishedFestivals: 0,
    approvedApplications: 0,
    revenue: 0,
  });

  const [recentFestivals, setRecentFestivals] = useState<any[]>([]);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [pendingFestivals, setPendingFestivals] = useState<any[]>([]);
  const [validating, setValidating] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'festival' | 'stand'; id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAdminEmail(user?.email)) {
      navigate('/home');
    }
  }, [user, authLoading, navigate]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [
        usersSnap,
        festivalsSnap,
        standsSnap,
        applicationsSnap,
        conversationsSnap,
        favoritesSnap,
        publishedSnap,
        approvedSnap
      ] = await Promise.all([
        getCountFromServer(collection(db, 'users')),
        getCountFromServer(collection(db, 'festivals')),
        getCountFromServer(collection(db, 'stands')),
        getCountFromServer(collection(db, 'applications')),
        getCountFromServer(collection(db, 'conversations')),
        getCountFromServer(collection(db, 'favorites')),
        getCountFromServer(query(collection(db, 'festivals'), where('status', '==', 'published'))),
        getCountFromServer(query(collection(db, 'applications'), where('status', '==', 'confirme')))
      ]);

      // CA : somme des paiements confirmés (champ amount en centimes, sinon 29€ par défaut)
      const confirmedDocs = await getDocs(query(collection(db, 'applications'), where('status', '==', 'confirme')));
      const revenue = confirmedDocs.docs.reduce((sum, d) => {
        const a = d.data().amount;
        return sum + (typeof a === 'number' ? a / 100 : 29);
      }, 0);

      setStats({
        totalUsers: usersSnap.data().count,
        totalFestivals: festivalsSnap.data().count,
        totalStands: standsSnap.data().count,
        totalApplications: applicationsSnap.data().count,
        totalConversations: conversationsSnap.data().count,
        totalFavorites: favoritesSnap.data().count,
        publishedFestivals: publishedSnap.data().count,
        approvedApplications: approvedSnap.data().count,
        revenue,
      });

      // Fetch recent festivals
      const fQuery = query(collection(db, 'festivals'), orderBy('createdAt', 'desc'), limit(5));
      const fDocs = await getDocs(fQuery);
      setRecentFestivals(fDocs.docs.map(d => ({ id: d.id, ...d.data() })));

      // Fetch recent stands
      const sQuery = query(collection(db, 'stands'), orderBy('createdAt', 'desc'), limit(5));
      const sDocs = await getDocs(sQuery);
      setRecentUsers(sDocs.docs.map(d => ({ id: d.id, ...d.data() })));

      // Fetch festivals pending review
      const pendingQuery = query(collection(db, 'festivals'), where('status', '==', 'pending_review'));
      const pendingDocs = await getDocs(pendingQuery);
      setPendingFestivals(pendingDocs.docs.map(d => ({ id: d.id, ...d.data() })));

    } catch (err) {
      console.error("Error loading admin stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdminEmail(user?.email)) {
      loadStats();
    }
  }, [user]);

  const handleValidateFestival = async (festivalId: string, action: 'approve' | 'reject') => {
    if (action === 'reject' && !rejectReason.trim()) {
      alert('Veuillez indiquer un motif de refus.');
      return;
    }
    setValidating(festivalId + action);
    try {
      const token = await user!.getIdToken();
      const resp = await fetch('/api/admin/validate-festival', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ festivalId, action, rejectionReason: rejectReason }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      setPendingFestivals(prev => prev.filter(f => f.id !== festivalId));
      setRejectReason('');
    } catch (err: any) {
      alert('Erreur : ' + err.message);
    } finally {
      setValidating(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const token = await user!.getIdToken();
      const endpoint = deleteTarget.type === 'festival' ? '/api/admin/delete-festival' : '/api/admin/delete-stand';
      const body = deleteTarget.type === 'festival' ? { festivalId: deleteTarget.id } : { standId: deleteTarget.id };
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');

      if (deleteTarget.type === 'festival') {
        setRecentFestivals(prev => prev.filter(f => f.id !== deleteTarget.id));
      } else {
        setRecentUsers(prev => prev.filter(s => s.id !== deleteTarget.id));
      }
      setDeleteTarget(null);
    } catch (err: any) {
      alert('Erreur : ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading || (loading && stats.totalUsers === 0)) {
    return (
      <div className="flex h-[80vh] items-center justify-center text-white">
        <RefreshCw className="w-8 h-8 animate-spin text-[#ff5a00]" />
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, subtext, colorClass }: any) => (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 backdrop-blur-md border border-white/10 rounded-2xl p-6 relative overflow-hidden"
    >
      <div className={`absolute top-0 right-0 w-32 h-32 blur-3xl opacity-20 -mr-10 -mt-10 ${colorClass}`} />
      <div className="flex items-start justify-between relative z-10">
        <div>
          <p className="text-gray-400 font-medium mb-1 text-sm">{title}</p>
          <h3 className="text-4xl font-black text-white tracking-tight">{value}</h3>
          {subtext && <p className="text-xs text-gray-500 mt-2">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-white/5`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 pb-32">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3">
            <Shield className="w-8 h-8 text-red-500" />
            Control Center
          </h1>
          <p className="text-gray-400 mt-1">Supervision globale de la plateforme FESTIN en temps réel.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/admin/promoters')}
            className="flex items-center gap-2 px-4 py-2 bg-[#FF5C00]/10 hover:bg-[#FF5C00]/20 text-[#FF5C00] rounded-lg transition-colors border border-[#FF5C00]/20 text-sm font-bold"
          >
            <Handshake className="w-4 h-4" />
            Promoteurs
          </button>
          <button
            onClick={loadStats}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors border border-white/10 text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
        </div>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard 
          title="Utilisateurs" 
          value={stats.totalUsers} 
          icon={Users} 
          colorClass="bg-blue-500" 
          subtext="Comptes créés"
        />
        <StatCard 
          title="Festivals Publiés" 
          value={stats.publishedFestivals} 
          icon={Tent} 
          colorClass="bg-[#ff5a00]" 
          subtext={`Sur ${stats.totalFestivals} au total`}
        />
        <StatCard 
          title="Stands" 
          value={stats.totalStands} 
          icon={Store} 
          colorClass="bg-[#4b3cfa]" 
          subtext="Profils exposants"
        />
        <StatCard
          title="Contrats Confirmés"
          value={stats.approvedApplications}
          icon={FileText}
          colorClass="bg-emerald-500"
          subtext={`Sur ${stats.totalApplications} candidatures`}
        />
        <StatCard
          title="Chiffre d'Affaires"
          value={`${stats.revenue.toLocaleString('fr-FR', { minimumFractionDigits: 0 })} €`}
          icon={Euro}
          colorClass="bg-yellow-500"
          subtext={`${stats.approvedApplications} × 29 € (offre lancement)`}
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="bg-zinc-900/30 border border-white/5 p-4 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-gray-400" />
            <span className="text-gray-300">Conversations</span>
          </div>
          <span className="text-xl font-bold text-white">{stats.totalConversations}</span>
        </div>
        <div className="bg-zinc-900/30 border border-white/5 p-4 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Star className="w-5 h-5 text-gray-400" />
            <span className="text-gray-300">Mises en Favoris</span>
          </div>
          <span className="text-xl font-bold text-white">{stats.totalFavorites}</span>
        </div>
        <div className="bg-zinc-900/30 border border-white/5 p-4 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-green-400" />
            <span className="text-gray-300">Statut Plateforme</span>
          </div>
          <span className="text-sm font-bold text-green-400 flex items-center gap-2">
             <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
             En Ligne
          </span>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Tent className="w-5 h-5 text-[#ff5a00]" /> 5 Derniers Festivals Créés
          </h2>
          <div className="space-y-3">
            {recentFestivals.map((fest, idx) => (
              <div
                key={idx}
                onClick={() => navigate(`/festival/${fest.id}`)}
                className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 flex justify-between items-center cursor-pointer hover:bg-zinc-800/60 hover:border-white/20 transition-all group"
              >
                <div>
                  <h4 className="font-bold text-white">{fest.name}</h4>
                  <p className="text-xs text-gray-500">Par {fest.organizerName || 'Inconnu'} • {fest.location}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`px-2 py-1 rounded text-xs font-bold ${fest.status === 'published' ? 'bg-[#ff5a00]/20 text-[#ff5a00]' : 'bg-gray-800 text-gray-400'}`}>
                    {fest.status === 'published' ? 'Publié' : 'Brouillon'}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'festival', id: fest.id, name: fest.name || 'ce festival' }); }}
                    className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Supprimer ce festival"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-300 transition-colors" />
                </div>
              </div>
            ))}
            {recentFestivals.length === 0 && <p className="text-sm text-gray-500">Aucun festival récent.</p>}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Store className="w-5 h-5 text-[#4b3cfa]" /> 5 Derniers Stands Créés
          </h2>
          <div className="space-y-3">
            {recentUsers.map((stand, idx) => (
              <div
                key={idx}
                onClick={() => navigate(`/stand-detail/${stand.id}`)}
                className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 flex justify-between items-center cursor-pointer hover:bg-zinc-800/60 hover:border-white/20 transition-all group"
              >
                <div>
                  <h4 className="font-bold text-white">{stand.name}</h4>
                  <p className="text-xs text-gray-500">{stand.category || 'Non catégorisé'} • {stand.location || 'Pas de ville'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {stand.logo && (
                    <img src={stand.logo} alt="logo" className="w-8 h-8 rounded bg-zinc-800 object-cover" />
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'stand', id: stand.id, name: stand.name || 'ce stand' }); }}
                    className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Supprimer ce stand"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-300 transition-colors" />
                </div>
              </div>
            ))}
            {recentUsers.length === 0 && <p className="text-sm text-gray-500">Aucun profil de stand récent.</p>}
          </div>
        </div>
      </div>

      {/* ── Festivals en attente de validation ── */}
      <div className="mt-12">
        <h2 className="text-xl font-black text-white mb-2 flex items-center gap-3">
          <Clock className="w-6 h-6 text-amber-400" />
          Festivals en attente de validation
          {pendingFestivals.length > 0 && (
            <span className="bg-amber-400/20 text-amber-400 text-sm font-black px-2.5 py-0.5 rounded-full">
              {pendingFestivals.length}
            </span>
          )}
        </h2>
        <p className="text-sm text-gray-500 mb-6">Documents légaux soumis par les organisateurs — à examiner avant publication.</p>

        {pendingFestivals.length === 0 ? (
          <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-gray-400 font-medium">Aucun festival en attente de validation</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingFestivals.map(fest => (
              <div key={fest.id} className="bg-zinc-900/50 border border-amber-500/20 rounded-2xl p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="font-black text-white text-lg">{fest.name || 'Sans nom'}</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {fest.location} • {fest.startDate} → {fest.endDate}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Soumis le {fest.legalDocs?.submittedAt ? new Date(fest.legalDocs.submittedAt).toLocaleDateString('fr-FR') : '—'}
                    </p>
                  </div>
                  <span className="shrink-0 px-2.5 py-1 rounded-full text-[10px] font-black bg-amber-400/15 text-amber-400 border border-amber-400/20 uppercase">
                    En attente
                  </span>
                </div>

                {/* Documents */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                  {[
                    { label: 'Assurance événementielle', url: fest.legalDocs?.insuranceUrl, required: true },
                    { label: 'Autorisation municipale', url: fest.legalDocs?.municipalAuthUrl, required: false },
                    { label: 'Déclaration SACEM', url: fest.legalDocs?.sacemUrl, required: false },
                  ].map(doc => (
                    <div key={doc.label} className={`rounded-xl border p-3 ${doc.url ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-white/5 bg-white/3'}`}>
                      <p className="text-xs font-bold text-gray-300 mb-1.5">{doc.label}</p>
                      {doc.url ? (
                        <a href={doc.url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-emerald-400 font-medium hover:underline">
                          <ExternalLink className="w-3 h-3" /> Voir le document
                        </a>
                      ) : (
                        <p className="text-xs text-gray-600">{doc.required ? '⚠️ Manquant' : 'Non fourni'}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Reject reason input */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Motif de refus (obligatoire si refus)"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-white/20"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => handleValidateFestival(fest.id, 'approve')}
                    disabled={validating !== null}
                    className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-sm rounded-xl transition-all"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {validating === fest.id + 'approve' ? 'Validation…' : 'Approuver & Publier'}
                  </button>
                  <button
                    onClick={() => handleValidateFestival(fest.id, 'reject')}
                    disabled={validating !== null}
                    className="flex items-center gap-2 px-5 py-2.5 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 text-red-400 font-bold text-sm rounded-xl border border-red-500/20 transition-all"
                  >
                    <XCircle className="w-4 h-4" />
                    {validating === fest.id + 'reject' ? 'Refus…' : 'Refuser'}
                  </button>
                  <button
                    onClick={() => navigate(`/manage-festival/${fest.id}`)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 font-medium text-sm rounded-xl transition-all"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Voir dashboard
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modale de confirmation de suppression */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-6">
          <div className="bg-zinc-900 border border-red-500/20 rounded-3xl p-8 max-w-md w-full">
            <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-5">
              <AlertTriangle className="w-7 h-7 text-red-400" />
            </div>
            <h3 className="text-xl font-black text-white mb-2">
              Supprimer {deleteTarget.type === 'festival' ? 'ce festival' : 'ce stand'} ?
            </h3>
            <p className="text-sm text-gray-400 mb-1">
              <span className="font-bold text-white">{deleteTarget.name}</span> sera supprimé définitivement,
              ainsi que {deleteTarget.type === 'festival' ? 'toutes les candidatures reçues sur ce festival' : 'toutes ses candidatures envoyées'}.
            </p>
            <p className="text-xs text-red-400/80 mb-6">Cette action est irréversible.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 px-5 py-3 bg-white/5 hover:bg-white/10 text-gray-300 font-bold text-sm rounded-xl transition-all disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold text-sm rounded-xl transition-all"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
